import { fromPromise } from '@apollo/client';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { FetchResult } from '@apollo/client/link/core/types';
import { showToast } from '../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../constants/apollo-errors';
import { clearImpersonationStorage } from '../helpers/impersonationHelpers';
import { getNewAccessToken } from '../hooks/useAuth';
import { ApolloErrorMapKeysType } from '../models/apollo-errors';

let isRefreshing = false;
let pendingRequests: (() => void)[] = [];

type errorMessageType = ApolloErrorMapKeysType | 'You need to be logged to access this field';

const resolvePendingRequests = () => {
  // This just loops through each callback and resolves the promise.
  pendingRequests.forEach(callback => callback());
  pendingRequests = [];
};

const logout = async () => {
  localStorage.clear();
  window.location.href = '/login';
};

const handleAuthenticationError = (
  forward: ErrorResponse['forward'],
  operation: ErrorResponse['operation']
): Observable<FetchResult> => {
  if (!isRefreshing) {
    isRefreshing = true;
    // For the first request, we attempt to refresh the token before retrying the request.
    // If it fails to refresh, log the user out. If it succeeds, then complete all pending requests and retry
    // the original request.
    return fromPromise(
      getNewAccessToken().catch(async () => {
        await logout().catch(() => {
          /* prevents runtime error */
        });
      })
    ).flatMap(() => {
      isRefreshing = false;
      if (localStorage.getItem('token')) {
        // Localstorage will only have 'token' if the refresh was successful.
        // Only retry pending requests if we are still logged in.
        resolvePendingRequests();
        return forward(operation);
      } else {
        // Refresh didn't work, so clear pending requests so we don't try them again, and return an empty
        // observable so this first request doesn't retry and bubble up an error.
        pendingRequests = [];
        return fromPromise(
          new Promise(() => {
            //
          })
        );
      }
    });
  } else {
    // For each additional request, we want to delay the retry until the refresh is done.
    // -> Create a promise that adds a callback to pendingRequests that will resolve the promise
    // -> Return a new observable (fromPromise) based on this promise
    // -> Add a flatmap() callback to the observable that will forward (retry) the request
    // -> When refreshing is done, resolvePendingRequests() is called which loops through each pending request
    //    and resolves them
    // -> flatmap() is called and retries the request
    return fromPromise(
      new Promise<void>(resolve => {
        pendingRequests.push(() => resolve());
      })
    ).flatMap(() => {
      return forward(operation);
    });
  }
};

// Modified from https://stackoverflow.com/a/63386965
// The basis of how this works is that it will return an observable that is from a promise - flatmap() is only called
// when the promise is resolved.
// This means we can resolve the promise later, i.e. when the token refresh is done, and thus flatmap()
// will be called which retries the request.
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }): void | Observable<FetchResult> => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const errMessage: errorMessageType = err.message as errorMessageType;
        if (errMessage === 'SECURITY_USER_NOT_IN_ALPHA') {
          // This should render the legacy app as the refresh token won't be present.
          logout().catch(() => {
            /* prevents runtime error */
          });
        } else if (
          errMessage === 'You need to be logged to access this field' ||
          errMessage === 'SECURITY_NOT_AUTHENTICATED'
        ) {
          return handleAuthenticationError(forward, operation);
        }
      }
    }
    if (networkError && 'statusCode' in networkError && networkError.statusCode >= 500) {
      if (
        graphQLErrors &&
        graphQLErrors.length &&
        graphQLErrors[0].extensions.hasOwnProperty('gql_message') &&
        graphQLErrors[0].extensions.gql_message === 'SECURITY_IMPERSONATION_FAILED'
      ) {
        showToast({
          severity: 'error',
          summary: 'Impersonation Failed',
          detail: ''
        });
        clearImpersonationStorage();
        window.location.assign('/');
      } else {
        showToast({
          severity: 'error',
          detail: GENERIC_ERROR_MESSAGE,
          summary: 'Internal Server Error',
          sticky: true
        });
      }
    }
  }
);
